import React from "react";
import { useErrorBoundary } from "react-error-boundary";
import { Link, useSearchParams } from "react-router-dom";
import { SearchIcon } from "affinityIcons";
import { isNil } from "ramda";

import { NavigateButton } from "affinityUI/elements/button";
import { Grid } from "affinityUI/elements/grid";
import { EmptyState } from "affinityUI/features/emptyState";
import { Heading } from "affinityUI/structures/heading";
import { Layout } from "affinityUI/structures/layout";
import { useGetPatientsQuery } from "gql";
import { Restricted, useAuth } from "lib/auth";

import { PatientSearch } from "../../../app/components/patientSearch";
import { PatientCard } from "../../../views/patientDetails/patients/patientCard";

export const SearchPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { showBoundary: onError } = useErrorBoundary();
  const [{ isPermitted }] = useAuth();
  const isPermitAddPatient = isPermitted(["add_patient"]);
  const { loading, data } = useGetPatientsQuery({
    variables: { search: searchParams.get("term") },
    errorPolicy: "all",
    onError,
  });

  const actions = (
    <Restricted permits={["add_patient"]}>
      <Link to="add">Add patient</Link>
    </Restricted>
  );

  return (
    <Layout
      loading={loading}
      title="Search results"
      backLink="/patients"
      showBackBtn
      backLinkText="Back to patients page"
      actions={<PatientSearch />}
    >
      <Grid>
        {!isNil(data?.patients) && Boolean(data?.patients?.length) && (
          <>
            <Heading>
              <Heading.Meta data-testid="search-meta">
                <strong>
                  {data?.patients?.length} {data?.patients?.length === 1 ? "result" : "results"} based on &quot;
                  {searchParams.get("term")}&quot; have been found
                </strong>
              </Heading.Meta>
              <Heading.Actions>{actions}</Heading.Actions>
            </Heading>
            {data?.patients.map((patient) => (
              <PatientCard
                data-testid="search-list-item"
                key={patient.id}
                searchTerm={searchParams.get("term")}
                patient={patient}
              />
            ))}
          </>
        )}
        {!data?.patients?.length && (
          <EmptyState>
            <EmptyState.Icon>
              <SearchIcon />
            </EmptyState.Icon>
            <EmptyState.Message>
              <h3>No patients found</h3>
              Your search <strong>&quot;{searchParams.get("term")}&quot;</strong> does not match any existing
              information. You can search by Patient name, NHS / Hospital number or Patient I.D.
              {isPermitAddPatient && " Alternatively, you can add a new patient."}
            </EmptyState.Message>
            {isPermitAddPatient && (
              <EmptyState.Message>
                <NavigateButton to="../add">Create patient</NavigateButton>
              </EmptyState.Message>
            )}
          </EmptyState>
        )}
      </Grid>
    </Layout>
  );
};
